import type { FC } from "react";
import { useEffect } from "react";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { EBrandedNonBrandedValues } from "app/pages/website-analysis/traffic-sources/search/components/WebsiteKeywordsPageTypes";
import allSearchEngines from "app/pages/website-analysis/constants/searchEngines";
import { EllipsisDropdownItem } from "@similarweb/ui-components";
import { MultiIncludeExcludeKeywordsInline } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/MultiIncludeExcludeKeywordsInline";
import { MultiIncludeExcludeWebsitesInline } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/MultiIncludeExcludeWebsitesInline";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import type { IRule } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import allSearchTypes from "app/pages/website-analysis/constants/searchTypes";
import type { EChangeFilterValues } from "app/components/filters-bar/change-filter/ChangeFilter";
import Sentry from "@similarweb/sentry-logger";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import type {
    EPresetOption,
    PresetWithDisabled,
} from "app/common/keyword-research/website-keywords/competitiveFilterPresets";
import { usePresetsBoxesConfig } from "app/common/keyword-research/website-keywords/competitiveFilterPresets";
import { changeFilterItems } from "app/common/keyword-research/topics/topicsFilterConsts";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

export interface IPreset {
    text: string;
    id: string;
    disabled?: boolean;
}
const getChangeFilterItemsWithClear = () => [
    {
        id: undefined as EChangeFilterValues,
        text: translate("home.dashboards.wizard.filters.clicksChange.empty"),
    },
    ...changeFilterItems,
];

const orderByOptions = [
    { id: "Clicks desc", text: "Traffic Share" },
    { id: "ClicksChange desc", text: "Change" },
];

const BRANDED_NON_BRANDED_RESET = "RESET";

export const sourceTypes = [
    {
        id: SourceTypeMap.Total,
        text: EKeywordTabs[EKeywordTabs.Total],
    },
    {
        id: SourceTypeMap.Organic,
        text: EKeywordTabs[EKeywordTabs.Organic],
    },
    {
        id: SourceTypeMap.Paid,
        text: EKeywordTabs[EKeywordTabs.Paid],
    },
];
const getBrandedNonBrandedOptions = () => [
    {
        text: translate("analysis.source.search.keywords.filters.branded.default"),
        id: BRANDED_NON_BRANDED_RESET,
    },
    {
        text: translate("analysis.source.search.keywords.filters.branded.abtest"),
        id: EBrandedNonBrandedValues.IncludeBranded.toString(),
    },
    {
        text: translate("analysis.source.search.keywords.filters.non-branded.abtest"),
        id: EBrandedNonBrandedValues.IncludeNoneBranded.toString(),
    },
];

const getDropdownButtonText = (list: string[]) => {
    switch (list.length) {
        case 0:
            return translate("home.dashboards.wizard.filters.searchTypes.selectMultiple");
        case 1:
            return list[0];
        default:
            return translate("multiselect.chipdown.filters.one.item.plus.extra.items", {
                item: list[0],
                amount: list.length - 1,
            });
    }
};

type BrandedNonBrandedOptionId = ReturnType<typeof getBrandedNonBrandedOptions>[number]["id"];
type SourceTypeId = (typeof sourceTypes)[number]["id"];
const brandedNonBrandedOptionsToParamsMap: Record<
    BrandedNonBrandedOptionId,
    Record<"IncludeNoneBranded" | "IncludeBranded", boolean>
> = {
    [EBrandedNonBrandedValues.IncludeBranded]: {
        IncludeNoneBranded: false,
        IncludeBranded: true,
    },
    [EBrandedNonBrandedValues.IncludeNoneBranded]: {
        IncludeNoneBranded: true,
        IncludeBranded: false,
    },
    RESET: {
        IncludeNoneBranded: false,
        IncludeBranded: false,
    },
};

const DropDownSourcesFiltersItemComponent = ({ id, text, selectedIds, onClick }) => {
    const isSelected = selectedIds.includes(id);
    return (
        <EllipsisDropdownItem
            onClick={() => onClick(id)}
            showCheckBox
            selected={isSelected}
            key={id}
            id={id}
        >
            {translate(text)}
        </EllipsisDropdownItem>
    );
};

export const filterTrafficPresetItems = (items: PresetWithDisabled<EPresetOption>[]): IPreset[] => {
    const result = items.map(({ title, id, disabled }) => ({
        text: title,
        id,
        disabled,
    }));

    return [
        {
            text: translate("home.dashboards.wizard.filters.trafficPreset.empty"),
            id: "noSelection",
            disabled: undefined,
        },
        ...result,
    ];
};
interface ITableWebsiteKeywordsDashboardWidgetFiltersProps {
    widgetFilters: any;
    changeFilter: (filters: any) => void;
    widgetKeys: any;
    widgetWebSource: string;
    widgetCountry: number | string;
}

export const TableWebsiteKeywordsDashboardWidgetFilters: FC<ITableWebsiteKeywordsDashboardWidgetFiltersProps> = ({
    widgetFilters,
    changeFilter,
    widgetKeys,
    widgetWebSource,
    widgetCountry,
}) => {
    const trafficPresetOptions = usePresetsBoxesConfig(
        widgetKeys[0],
        widgetKeys.length > 1,
        widgetWebSource,
        widgetCountry === 999 || widgetCountry === "999",
    );

    const trafficPresetItems: IPreset[] = filterTrafficPresetItems(trafficPresetOptions);

    useEffect(() => {
        if (
            (widgetFilters.trafficPreset &&
                !trafficPresetItems.some((filter) => filter.id === widgetFilters.trafficPreset)) ||
            (widgetCountry === 999 &&
                trafficPresetItems.some((filter) => filter.id === widgetFilters.trafficPreset && filter.disabled))
        ) {
            changeFilter([
                ...Object.entries(widgetFilters).map(([name, value]) => ({ name, value })),
                {
                    name: "trafficPreset",
                    value: undefined,
                },
            ]);
        }
    }, [trafficPresetItems, widgetCountry]);

    const selectedEngines: string[] =
        widgetFilters?.family?.split(FILTER_DEFAULT_SEPARATOR).reduce((acc, item) => {
            try {
                acc.push(JSON.parse(item));
            } catch (e) {
                Sentry.error("Error parsing family filter", e);
            }
            return acc;
        }, []) ?? [];
    const selectedSearchTypes: string[] = widgetFilters?.source?.split(FILTER_DEFAULT_SEPARATOR) ?? [];

    let selectedKeywords: IRule[] = [];
    let selectedWebsites: IRule[] = [];
    try {
        if (widgetFilters.multiIncludeExcludeKeywords) {
            selectedKeywords = JSON.parse(widgetFilters.multiIncludeExcludeKeywords);
        }
        if (widgetFilters.multiIncludeExcludeWebsites) {
            selectedWebsites = JSON.parse(widgetFilters.multiIncludeExcludeWebsites);
        }
    } catch (e) {
        Sentry.error("Error parsing multiIncludeExclude filters", e);
    }

    const selectedBrandedNonBranded: BrandedNonBrandedOptionId =
        widgetFilters?.IncludeBranded === widgetFilters?.IncludeNoneBranded
            ? BRANDED_NON_BRANDED_RESET
            : widgetFilters?.IncludeBranded
              ? EBrandedNonBrandedValues.IncludeBranded.toFixed()
              : EBrandedNonBrandedValues.IncludeNoneBranded.toFixed();

    const selectedSourceType = widgetFilters?.sourceType ?? sourceTypes[0].id;
    const getSelectedFilters = () => {
        return Object.entries(widgetFilters).map(([name, value]) => ({ name, value }));
    };

    const handleEnginesChange = (engine: string) => {
        const value = selectedEngines.includes(engine)
            ? selectedEngines.filter((e) => e !== engine)
            : [...selectedEngines, engine];

        changeFilter([
            ...getSelectedFilters(),
            {
                name: "family",
                value: value.length ? value.map((item) => `"${item}"`).join(FILTER_DEFAULT_SEPARATOR) : undefined,
            },
        ]);
    };

    const handleSearchTypesChange = (type: string) => {
        const value = selectedSearchTypes.includes(type)
            ? selectedSearchTypes.filter((t) => t !== type)
            : [...selectedSearchTypes, type];

        changeFilter([
            ...getSelectedFilters(),
            {
                name: "source",
                value: value.length ? value.join(FILTER_DEFAULT_SEPARATOR) : undefined,
            },
        ]);
    };

    const handleMultiIncludeExcludeChange = (rules: IRule[], isKeywords: boolean) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: isKeywords ? "multiIncludeExcludeKeywords" : "multiIncludeExcludeWebsites",
                value: JSON.stringify(rules),
            },
        ]);
    };

    const handleBrandedNonBrandedChange = ({ id }: { id: BrandedNonBrandedOptionId }) => {
        const values = brandedNonBrandedOptionsToParamsMap[id];
        changeFilter([...getSelectedFilters(), ...Object.entries(values).map(([name, value]) => ({ name, value }))]);
    };
    const handleClicksSourceTypeChange = ({ id }: { id: SourceTypeId }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "sourceType",
                value: id,
            },
        ]);
    };

    const handleClicksChangeChange = ({ id }: { id: EChangeFilterValues }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "Change",
                value: id,
            },
        ]);
    };

    const handleOrderByChange = ({ id }: { id: string }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "orderBy",
                value: id,
            },
        ]);
    };
    const handleClicksChangeTrafficPreset = ({ id }: { id: string }) => {
        changeFilter([
            ...getSelectedFilters(),
            {
                name: "trafficPreset",
                value: id !== "noSelection" ? id : undefined,
            },
        ]);
    };

    return (
        <div className="filters">
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.searchTypes</I18n>
                </h5>
                <StatefulDropdown
                    items={allSearchTypes}
                    title={getDropdownButtonText(
                        selectedSearchTypes.map((id) => allSearchTypes.find((t) => t.id === id)?.text),
                    )}
                    DropDownItemComponent={(props) => (
                        <DropDownSourcesFiltersItemComponent
                            {...props}
                            selectedIds={selectedSearchTypes}
                            onClick={handleSearchTypesChange}
                        />
                    )}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.sourcesFilter</I18n>
                </h5>
                <StatefulDropdown
                    items={allSearchEngines}
                    hasSearch
                    title={getDropdownButtonText(selectedEngines)}
                    DropDownItemComponent={(props) => (
                        <DropDownSourcesFiltersItemComponent
                            {...props}
                            selectedIds={selectedEngines}
                            onClick={handleEnginesChange}
                        />
                    )}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.trafficType</I18n>
                </h5>
                <StatefulDropdown
                    items={sourceTypes}
                    selectedId={selectedSourceType}
                    onSelect={handleClicksSourceTypeChange}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.includeBranded</I18n>
                </h5>
                <StatefulDropdown
                    items={getBrandedNonBrandedOptions()}
                    selectedId={selectedBrandedNonBranded}
                    onSelect={handleBrandedNonBrandedChange}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.orderBy</I18n>
                </h5>
                <StatefulDropdown
                    items={orderByOptions}
                    selectedId={widgetFilters.orderBy ?? orderByOptions[0].id}
                    onSelect={handleOrderByChange}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.clicksChange</I18n>
                </h5>
                <StatefulDropdown
                    items={getChangeFilterItemsWithClear()}
                    selectedId={widgetFilters.Change}
                    onSelect={handleClicksChangeChange}
                />
            </div>
            <div className="dynamicFilter">
                <I18n component="h5">home.dashboards.wizard.filters.trafficPreset</I18n>
                <StatefulDropdown
                    items={trafficPresetItems}
                    selectedId={
                        widgetFilters.trafficPreset &&
                        trafficPresetItems.some((filter) => filter.id === widgetFilters.trafficPreset)
                            ? widgetFilters.trafficPreset
                            : trafficPresetItems[0].id
                    }
                    onSelect={handleClicksChangeTrafficPreset}
                />
            </div>
            <div
                className="dynamicFilter"
                style={{ width: "100%" }}
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.multiIncludeExcludeKeywords</I18n>
                </h5>
                <MultiIncludeExcludeKeywordsInline
                    rules={selectedKeywords}
                    onMultiIncludeExcludeChange={(rules) => handleMultiIncludeExcludeChange(rules, true)}
                />
            </div>
            <div
                className="dynamicFilter"
                style={{ width: "100%" }}
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.multiIncludeExcludeWebsites</I18n>
                </h5>
                <MultiIncludeExcludeWebsitesInline
                    rules={selectedWebsites}
                    onMultiIncludeExcludeChange={(rules) => handleMultiIncludeExcludeChange(rules, false)}
                />
            </div>
        </div>
    );
};
